import { useCustomerPageContext } from "@contexts/customer"
import {
  channelsGetAllPlatform,
  listAiMessagesQuery,
  listAiMessageStepsQuery
} from "@domain/ai_answers"
import {
  deleteTestConversationMutation,
  getActionsForCustomerQuery,
  getAiGuidanceListQuery,
  getAiKnowledgeSourceContentQuery,
  getAiProfileQuery,
  getAllAiKnowledgeSourcesQuery,
  getMessageForAiSimulationQuery,
  testAiWithConversationMutation
} from "@domain/customer/ai_test"
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome"
import BoltIcon from "@mui/icons-material/Bolt"
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline"
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined"
import HelpOutlineIcon from "@mui/icons-material/HelpOutline"
import LockOutlinedIcon from "@mui/icons-material/LockOutlined"
import MenuBookIcon from "@mui/icons-material/MenuBook"
import ModeCommentOutlinedIcon from "@mui/icons-material/ModeCommentOutlined"
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore"
import NavigateNextIcon from "@mui/icons-material/NavigateNext"
import PersonOutlineIcon from "@mui/icons-material/PersonOutline"
import RefreshIcon from "@mui/icons-material/Refresh"
import RuleIcon from "@mui/icons-material/Rule"
import SearchIcon from "@mui/icons-material/Search"
import SendIcon from "@mui/icons-material/Send"
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined"
import TimelineIcon from "@mui/icons-material/Timeline"
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  ListSubheader,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography
} from "@mui/material"
import AiStepsDrawer from "@pages/main/ai_answers/sub_components/AiStepsDrawer"
import { useQuery } from "@tanstack/react-query"
import { showErrorToast, showSuccessToast } from "@utils/toast"
import {
  Action,
  AiGuidance,
  AiGuidanceStatusEnum,
  AiKnowledgeSource,
  AiKnowledgeSourcesSourceTypes,
  AiKnowledgeSourcesStatus,
  ChannelCommonTypes,
  Customer,
  MessageForAiSimulationData,
  MessageType,
  PlatformType,
  PreviousMessagePreview,
  PreviousMessagesRole,
  PreviousMessagesType
} from "__generated__/graphql"
import { ReactNode, useEffect, useRef, useState } from "react"
import styles from "./styles"

type SimulationType = "chat" | "comment" | "question"

type ChatMessage = {
  id: string
  role: PreviousMessagesRole
  content: string
  aiMessageId?: string
  answerId?: string
  request?: LastSimulationRequest
}

type SimulationHistoryMessage = Pick<ChatMessage, "id" | "role" | "content">

type LastSimulationRequest = {
  queryText: string
  history: SimulationHistoryMessage[]
  simulationType: SimulationType
  commentFields: CommentFields
  questionFields: QuestionFields
  pageUrl: string
  selectedChannel?: ChannelCommonTypes
}

type CommentFields = {
  postDescription: string
  postImageUrl: string
  postUrl: string
}

type QuestionFields = {
  productName: string
  productImageUrl: string
  productLink: string
  productCode: string
}

const EMPTY_COMMENT_FIELDS: CommentFields = {
  postDescription: "",
  postImageUrl: "",
  postUrl: ""
}

const EMPTY_QUESTION_FIELDS: QuestionFields = {
  productName: "",
  productImageUrl: "",
  productLink: "",
  productCode: ""
}

type AiSimTemplateData = {
  simulationType: SimulationType
  commentFields: CommentFields
  questionFields: QuestionFields
  inputValue: string
  dbDriverId?: string
  pageUrl?: string
}

type AiSimTemplate = {
  id: string
  name: string
  icon: string
  data: AiSimTemplateData
  isCustom?: boolean
}

const AI_SIM_TEMPLATES_STORAGE_KEY = "ai-simulation-templates"

const DEFAULT_AI_SIM_TEMPLATES: AiSimTemplate[] = [
  {
    id: "chat-basic",
    name: "Chat Test",
    icon: "💬",
    data: {
      simulationType: "chat",
      commentFields: EMPTY_COMMENT_FIELDS,
      questionFields: EMPTY_QUESTION_FIELDS,
      inputValue: "Merhaba, ürünle ilgili yardım alabilir miyim?"
    }
  },
  {
    id: "instagram-comment",
    name: "Instagram Comment Test",
    icon: "📷",
    data: {
      simulationType: "comment",
      commentFields: {
        postDescription: "5 numaralı ürünümüz şubemizde",
        postImageUrl:
          "https://qpien-dev.s3.us-east-2.amazonaws.com/urlInfo/instagram/17870534450718480-6a604c828be5a11684412fc3",
        postUrl:
          "https://www.instagram.com/reel/Ch9_dkxD54F/?utm_source=ig_web_copy_link"
      },
      questionFields: EMPTY_QUESTION_FIELDS,
      inputValue: "Bu ürün hala satışta mı?"
    }
  },
  {
    id: "hepsiburada-question",
    name: "Hepsiburada Question Test",
    icon: "🛍️",
    data: {
      simulationType: "question",
      commentFields: EMPTY_COMMENT_FIELDS,
      questionFields: {
        productName:
          "Porlistan Amara Çift Kapak Emaye Çaydanlık Takımı, Desenli Emaye Çaydanlık Seti",
        productImageUrl:
          "https://productimages.hepsiburada.net/s/777/1100/110000711893880.jpg",
        productLink: "https://hepsiburada.com/a-p-HBCV00006JH95Q",
        productCode: "HBCV00006JH95Q"
      },
      inputValue: "Bu ürün ne zaman gelir?"
    }
  }
]

const COMMENT_CHANNEL_TYPES = [
  PlatformType.Instagram,
  PlatformType.Facebook,
  PlatformType.Tiktok
]

const QUESTION_CHANNEL_TYPES = [
  PlatformType.Trendyol,
  PlatformType.Hepsiburada,
  PlatformType.N11,
  PlatformType.Pazarama,
  PlatformType.Ebay,
  PlatformType.Kaufland,
  PlatformType.Walmart,
  PlatformType.Amazon
]

const MARKETPLACE_LOOKUP_CHANNEL_TYPES = [
  PlatformType.Trendyol,
  PlatformType.Hepsiburada,
  PlatformType.N11
]

const HEPSIBURADA_SKU_CHANNEL_TYPES = [PlatformType.Hepsiburada]

const PLATFORM_ICONS: Partial<Record<PlatformType, string>> = {
  [PlatformType.Instagram]: "📷",
  [PlatformType.Facebook]: "📘",
  [PlatformType.Tiktok]: "🎵",
  [PlatformType.LiveChat]: "💭",
  [PlatformType.Trendyol]: "🟠",
  [PlatformType.Hepsiburada]: "🧡",
  [PlatformType.N11]: "🟣",
  [PlatformType.Pazarama]: "🔵",
  [PlatformType.Ebay]: "🟡",
  [PlatformType.Kaufland]: "🔴",
  [PlatformType.Walmart]: "🔷",
  [PlatformType.Amazon]: "🟤"
}

// Matches AiPersonMemory.conversationHistory's $slice: -20 cap in production.
const MAX_HISTORY = 20
const ACTIONS_PAGE_SIZE = 20
const KNOWLEDGE_PAGE_SIZE = 20

let messageIdCounter = 0
function nextMessageId() {
  messageIdCounter += 1
  return `ai-sim-msg-${messageIdCounter}`
}

function findDefaultChannelForType(
  simulationType: SimulationType,
  allChannels: ChannelCommonTypes[]
): ChannelCommonTypes | undefined {
  if (simulationType === "chat") {
    return allChannels.find(
      (channel) => channel?.channelType === PlatformType.LiveChat
    )
  }
  const relevantTypes =
    simulationType === "comment"
      ? COMMENT_CHANNEL_TYPES
      : QUESTION_CHANNEL_TYPES
  return allChannels.find(
    (channel) =>
      channel?.channelType &&
      relevantTypes.includes(channel.channelType as PlatformType)
  )
}

function groupChannelsByType(
  list: ChannelCommonTypes[]
): [string, ChannelCommonTypes[]][] {
  const map = new Map<string, ChannelCommonTypes[]>()
  list.forEach((channel) => {
    if (!channel?.channelType) {
      return
    }
    const group = map.get(channel.channelType) || []
    group.push(channel)
    map.set(channel.channelType, group)
  })
  return Array.from(map.entries())
}

function stepTestMsgTypeFor(simulationType: SimulationType): MessageType {
  if (simulationType === "comment") {
    return MessageType.Comment
  }
  if (simulationType === "question") {
    return MessageType.Question
  }
  return MessageType.TextMessage
}

function isActionEligible(action: Action, channelId: string): boolean {
  return (
    !!channelId &&
    !!action.channelIds?.length &&
    action.channelIds.includes(channelId)
  )
}

function guidanceSurfaceFor(messageType: MessageType): string {
  return messageType === MessageType.Comment
    ? "COMMENT"
    : (messageType as string)
}

function isGuidanceEligible(
  guidance: AiGuidance,
  channelId: string,
  messageType: MessageType
): boolean {
  if (guidance.status !== AiGuidanceStatusEnum.Active) {
    return false
  }
  const channelOk =
    !guidance.channel?.length ||
    (!!channelId && guidance.channel.includes(channelId))
  const surface = guidanceSurfaceFor(messageType)
  const messageTypeOk =
    !guidance.messageTypes?.length ||
    guidance.messageTypes.some((mt) => (mt as string) === surface)
  return channelOk && messageTypeOk
}

const KNOWLEDGE_STATUS_COLOR: Partial<
  Record<AiKnowledgeSourcesStatus, "success" | "error" | "warning">
> = {
  [AiKnowledgeSourcesStatus.Completed]: "success",
  [AiKnowledgeSourcesStatus.Failed]: "error",
  [AiKnowledgeSourcesStatus.InProgress]: "warning"
}

export type AiSimulationPageProps = {
  // When omitted, the component falls back to the customer detail page's
  // CustomerPageContext (route param-driven). AI Hub passes these explicitly
  // so the same UI can drive an operator-selected customer instead.
  customer?: Customer
  translate?: (key: string, options?: Record<string, unknown>) => string
  customerPicker?: ReactNode
  // Pre-fills the "fetch by message id" box and auto-runs it once the
  // customer/AI profile are ready — used by the AI Logs quick-simulate link.
  initialMessageId?: string
}

export default function CustomerAiTestPage(props: AiSimulationPageProps = {}) {
  const customerPageContext = useCustomerPageContext()
  const translate = props.translate ?? customerPageContext.translate
  const currentCustomer = props.customer ?? customerPageContext.currentCustomer
  const customerPicker = props.customerPicker
  const customerId = currentCustomer?._id

  const [simulationType, setSimulationType] = useState<SimulationType>("chat")
  const [commentFields, setCommentFields] =
    useState<CommentFields>(EMPTY_COMMENT_FIELDS)
  const [questionFields, setQuestionFields] = useState<QuestionFields>(
    EMPTY_QUESTION_FIELDS
  )
  const [pageUrl, setPageUrl] = useState("")
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [inputValue, setInputValue] = useState("")
  const [isSending, setIsSending] = useState(false)
  const [meta, setMeta] = useState<{
    conversationId?: string
    personId?: string
    aiPersonMemoryId?: string
  }>({})
  const [messageIdInput, setMessageIdInput] = useState("")
  const [isFetchingMessage, setIsFetchingMessage] = useState(false)
  const [fetchMessageError, setFetchMessageError] = useState<string>()
  const [customTemplates, setCustomTemplates] = useState<AiSimTemplate[]>([])
  const [showSaveTemplateDialog, setShowSaveTemplateDialog] = useState(false)
  const [templateName, setTemplateName] = useState("")
  const [stepsAiMessageId, setStepsAiMessageId] = useState<string | null>(null)
  const [stepsAnswerId, setStepsAnswerId] = useState<string | undefined>(
    undefined
  )
  const [selectedDbDriverId, setSelectedDbDriverId] = useState("")
  const [channelWarning, setChannelWarning] = useState<string>()
  const [selectedAction, setSelectedAction] = useState<Action | null>(null)
  const [selectedGuidance, setSelectedGuidance] = useState<AiGuidance | null>(
    null
  )
  const [selectedKnowledge, setSelectedKnowledge] =
    useState<AiKnowledgeSource | null>(null)
  const [actionsPage, setActionsPage] = useState(1)
  const [actionsSearchInput, setActionsSearchInput] = useState("")
  const [guidanceSearchInput, setGuidanceSearchInput] = useState("")
  const [guidanceAppliedSearch, setGuidanceAppliedSearch] = useState("")
  const [knowledgePage, setKnowledgePage] = useState(1)
  const [knowledgeSearchInput, setKnowledgeSearchInput] = useState("")
  const [knowledgeAppliedSearch, setKnowledgeAppliedSearch] = useState("")
  const [knowledgeStatusFilter, setKnowledgeStatusFilter] = useState<
    AiKnowledgeSourcesStatus | ""
  >("")
  const [knowledgeSourceTypeFilter, setKnowledgeSourceTypeFilter] = useState<
    AiKnowledgeSourcesSourceTypes | ""
  >("")
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const autoFetchedMessageIdRef = useRef<string | null>(null)

  const hasAiFeature = !!currentCustomer?.package?.features?.ai
  const stepTestMsgType = stepTestMsgTypeFor(simulationType)

  const { data: channels } = useQuery({
    queryKey: ["ai-test-channels", customerId],
    queryFn: async () => {
      if (!customerId) {
        return []
      }
      const response = await channelsGetAllPlatform({ customerId })
      return response.isSuccess ? response.data : []
    },
    enabled: !!customerId,
    refetchOnWindowFocus: false,
    refetchOnMount: false
  })

  function resolveChannelName(id: string): string {
    const found = (channels || []).find((channel) => channel?._id === id)
    return found?.name || id
  }

  const relevantChannelTypes: PlatformType[] | null =
    simulationType === "comment"
      ? COMMENT_CHANNEL_TYPES
      : simulationType === "question"
        ? QUESTION_CHANNEL_TYPES
        : null
  const availableChannels = (channels || []).filter(
    (channel): channel is ChannelCommonTypes =>
      !!channel?.channelType &&
      (relevantChannelTypes === null ||
        relevantChannelTypes.includes(channel.channelType as PlatformType))
  )
  const groupedChannels = groupChannelsByType(availableChannels)
  const selectedChannel = availableChannels.find(
    (channel) => channel?._id === selectedDbDriverId
  )

  const { data: stepsData, isLoading: isStepsLoading } = useQuery({
    queryKey: ["ai-test-steps", stepsAiMessageId, stepsAnswerId],
    queryFn: async () => {
      if (!stepsAiMessageId) {
        return undefined
      }
      const response = await listAiMessageStepsQuery(
        stepsAiMessageId,
        stepsAnswerId
      )
      return response.isSuccess ? response.data : undefined
    },
    enabled: !!stepsAiMessageId,
    refetchOnWindowFocus: false
  })

  const { data: aiProfile, isLoading: isAiProfileLoading } = useQuery({
    queryKey: ["ai-test-profile", customerId],
    queryFn: async () => {
      if (!customerId) {
        return undefined
      }
      const response = await getAiProfileQuery(customerId)
      if (!response.isSuccess) {
        return undefined
      }
      return response.data
    },
    enabled: !!customerId && hasAiFeature,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false
  })

  const {
    data: actionsData,
    isLoading: isActionsLoading,
    isFetched: isActionsFetched,
    refetch: refetchActions
  } = useQuery({
    queryKey: ["ai-test-actions", customerId, actionsPage],
    queryFn: async () => {
      if (!customerId) {
        return undefined
      }
      const response = await getActionsForCustomerQuery(
        customerId,
        actionsPage,
        ACTIONS_PAGE_SIZE
      )
      return response.isSuccess ? response.data : undefined
    },
    enabled: false,
    refetchOnWindowFocus: false
  })

  const filteredActions = (actionsData?.docs || []).filter(
    (action) =>
      !actionsSearchInput.trim() ||
      action.name
        ?.toLowerCase()
        .includes(actionsSearchInput.trim().toLowerCase())
  )

  const {
    data: guidanceData,
    isLoading: isGuidanceLoading,
    isFetched: isGuidanceFetched,
    refetch: refetchGuidance
  } = useQuery({
    queryKey: ["ai-test-guidance", customerId, guidanceAppliedSearch],
    queryFn: async () => {
      if (!customerId) {
        return []
      }
      const response = await getAiGuidanceListQuery(
        customerId,
        guidanceAppliedSearch || undefined
      )
      return response.isSuccess ? response.data : []
    },
    enabled: false,
    refetchOnWindowFocus: false
  })

  const {
    data: knowledgeData,
    isLoading: isKnowledgeLoading,
    isFetched: isKnowledgeFetched,
    refetch: refetchKnowledge
  } = useQuery({
    queryKey: [
      "ai-test-knowledge",
      customerId,
      knowledgePage,
      knowledgeAppliedSearch,
      knowledgeStatusFilter,
      knowledgeSourceTypeFilter
    ],
    queryFn: async () => {
      if (!customerId) {
        return undefined
      }
      const response = await getAllAiKnowledgeSourcesQuery({
        customerId,
        pageNumber: knowledgePage,
        pageSize: KNOWLEDGE_PAGE_SIZE,
        search: knowledgeAppliedSearch || undefined,
        status: knowledgeStatusFilter || undefined,
        sourceType: knowledgeSourceTypeFilter || undefined
      })
      return response.isSuccess ? response.data : undefined
    },
    enabled: false,
    refetchOnWindowFocus: false
  })

  const {
    data: knowledgeContentData,
    isLoading: isKnowledgeContentLoading,
    isFetched: isKnowledgeContentFetched,
    refetch: refetchKnowledgeContent
  } = useQuery({
    queryKey: ["ai-test-knowledge-content", customerId, selectedKnowledge?._id],
    queryFn: async () => {
      if (!customerId || !selectedKnowledge?._id) {
        return []
      }
      const response = await getAiKnowledgeSourceContentQuery(
        customerId,
        selectedKnowledge._id
      )
      return response.isSuccess ? response.data : []
    },
    enabled: false,
    refetchOnWindowFocus: false
  })

  useEffect(() => {
    if (isActionsFetched) {
      refetchActions()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionsPage])

  useEffect(() => {
    if (isGuidanceFetched) {
      refetchGuidance()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guidanceAppliedSearch])

  useEffect(() => {
    if (isKnowledgeFetched) {
      refetchKnowledge()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    knowledgePage,
    knowledgeAppliedSearch,
    knowledgeStatusFilter,
    knowledgeSourceTypeFilter
  ])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [messages, isSending])

  useEffect(() => {
    const stored = localStorage.getItem(AI_SIM_TEMPLATES_STORAGE_KEY)
    if (stored) {
      try {
        setCustomTemplates(JSON.parse(stored) as AiSimTemplate[])
      } catch (error) {
        console.error("Failed to load AI simulation templates:", error)
      }
    }
  }, [])

  useEffect(() => {
    if (selectedDbDriverId || !channels) {
      return
    }
    const defaultChannel = findDefaultChannelForType(simulationType, channels)
    if (defaultChannel?._id) {
      setSelectedDbDriverId(defaultChannel._id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [simulationType, channels])

  useEffect(() => {
    const targetMessageId = props.initialMessageId
    if (
      !targetMessageId ||
      !customerId ||
      !hasAiFeature ||
      autoFetchedMessageIdRef.current === targetMessageId
    ) {
      return
    }
    autoFetchedMessageIdRef.current = targetMessageId
    setMessageIdInput(targetMessageId)
    handleFetchMessage(targetMessageId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.initialMessageId, customerId, hasAiFeature])

  const allTemplates = [...DEFAULT_AI_SIM_TEMPLATES, ...customTemplates]

  function applyChannelSelection(dbDriverId: string | undefined) {
    setChannelWarning(undefined)
    if (!dbDriverId) {
      setSelectedDbDriverId("")
      return
    }
    if (!channels?.length) {
      setSelectedDbDriverId(dbDriverId)
      return
    }
    const matched = (channels || []).find(
      (channel) => channel?._id === dbDriverId
    )
    if (matched?._id) {
      setSelectedDbDriverId(matched._id)
    } else {
      setSelectedDbDriverId("")
      setChannelWarning(translate("ai_simulation_channel_removed_warning"))
    }
  }

  function handleSimulationTypeChange(value: SimulationType | null) {
    if (!value) {
      return
    }
    setSimulationType(value)
    const defaultChannel = findDefaultChannelForType(value, channels || [])
    applyChannelSelection(defaultChannel?._id || undefined)
  }

  function applyTemplate(template: AiSimTemplate) {
    setSimulationType(template.data.simulationType)
    setCommentFields(template.data.commentFields)
    setQuestionFields(template.data.questionFields)
    setInputValue(template.data.inputValue)
    setPageUrl(template.data.pageUrl || "")

    const allChannels = channels || []
    const savedChannel = template.data.dbDriverId
      ? allChannels.find((channel) => channel?._id === template.data.dbDriverId)
      : undefined
    const targetChannel =
      savedChannel ||
      findDefaultChannelForType(template.data.simulationType, allChannels)

    setChannelWarning(undefined)
    setSelectedDbDriverId(targetChannel?._id || "")

    showSuccessToast(translate("ai_simulation_template_applied"))
  }

  function saveAsTemplate() {
    if (!templateName.trim()) {
      showErrorToast(translate("ai_simulation_template_name_required"))
      return
    }

    const newTemplate: AiSimTemplate = {
      id: `custom-${Date.now()}`,
      name: templateName.trim(),
      icon:
        simulationType === "comment"
          ? "📷"
          : simulationType === "question"
            ? "🛍️"
            : "💬",
      data: {
        simulationType,
        commentFields,
        questionFields,
        inputValue,
        dbDriverId: selectedDbDriverId || undefined,
        pageUrl: pageUrl || undefined
      },
      isCustom: true
    }

    const updatedTemplates = [...customTemplates, newTemplate]
    setCustomTemplates(updatedTemplates)
    localStorage.setItem(
      AI_SIM_TEMPLATES_STORAGE_KEY,
      JSON.stringify(updatedTemplates)
    )
    showSuccessToast(translate("ai_simulation_template_saved"))
    setTemplateName("")
    setShowSaveTemplateDialog(false)
  }

  function deleteTemplate(templateId: string) {
    const updatedTemplates = customTemplates.filter((t) => t.id !== templateId)
    setCustomTemplates(updatedTemplates)
    localStorage.setItem(
      AI_SIM_TEMPLATES_STORAGE_KEY,
      JSON.stringify(updatedTemplates)
    )
    showSuccessToast(translate("ai_simulation_template_deleted"))
  }

  async function handleReset() {
    if (meta.conversationId || meta.aiPersonMemoryId) {
      await deleteTestConversationMutation({
        conversationId: meta.conversationId,
        aiPersonMemoryId: meta.aiPersonMemoryId
      })
    }
    setMessages([])
    setMeta({})
  }

  async function handleFetchMessage(overrideMessageId?: string) {
    const messageId = (overrideMessageId ?? messageIdInput).trim()
    if (!messageId || !customerId || isFetchingMessage) {
      return
    }

    setIsFetchingMessage(true)
    setFetchMessageError(undefined)

    const response = await getMessageForAiSimulationQuery(customerId, messageId)

    setIsFetchingMessage(false)

    if (!response.isSuccess || !response.data) {
      setFetchMessageError(
        response.errorMessage || translate("ai_simulation_message_not_found")
      )
      return
    }

    const message: MessageForAiSimulationData = response.data

    if (message.messageType === MessageType.Comment) {
      setSimulationType("comment")
      setCommentFields({
        postDescription: message.commentData?.postDescription || "",
        postImageUrl: message.commentData?.postImageUrl || "",
        postUrl: message.commentData?.postUrl || ""
      })
    } else if (message.messageType === MessageType.Question) {
      setSimulationType("question")
      setQuestionFields({
        productName: message.questionData?.productName || "",
        productImageUrl: message.questionData?.productImageUrl || "",
        productLink: message.questionData?.productUrl || "",
        productCode:
          message.questionData?.productMainId || message.questionData?.sku || ""
      })
    } else {
      setSimulationType("chat")
    }

    applyChannelSelection(message.channel || undefined)
    setInputValue(message.content || "")

    // Start a fresh test conversation seeded with this message's real prior
    // history (if any), so sending reproduces the same context the AI would see.
    if (meta.conversationId || meta.aiPersonMemoryId) {
      await deleteTestConversationMutation({
        conversationId: meta.conversationId,
        aiPersonMemoryId: meta.aiPersonMemoryId
      })
    }
    setMeta({})
    const historyMessages: ChatMessage[] = (message.previousMessages || [])
      .filter(
        (entry): entry is PreviousMessagePreview => !!entry && !!entry._id
      )
      .map((entry) => ({
        id: nextMessageId(),
        role:
          entry.role === "assistant"
            ? PreviousMessagesRole.Assistant
            : PreviousMessagesRole.User,
        content: entry.content,
        // Historical AI replies resolve their step log via answerId (the
        // message's own _id) — see listAiMessageSteps' answerId lookup path.
        answerId: entry.isAiGenerated ? entry._id : undefined
      }))
    setMessages(historyMessages)
  }

  async function runSimulation(
    request: LastSimulationRequest,
    options?: {
      metaOverride?: typeof meta
    }
  ) {
    if (!customerId || !aiProfile?._id || !request.selectedChannel?._id) {
      return
    }

    setIsSending(true)
    const response = await testAiWithConversationMutation({
      customerId,
      query: request.queryText,
      aiProfileId: aiProfile._id,
      conversationId: options?.metaOverride?.conversationId,
      personId: options?.metaOverride?.personId,
      aiPersonMemoryId: options?.metaOverride?.aiPersonMemoryId,
      content: request.history.map<PreviousMessagesType>((message) => ({
        role: message.role,
        content: message.content
      })),
      stepTestMsgType: stepTestMsgTypeFor(request.simulationType),
      ...(request.simulationType === "comment" && {
        commentData: request.commentFields
      }),
      ...(request.simulationType === "question" && {
        questionData: {
          productName: request.questionFields.productName || undefined,
          productImageUrl: request.questionFields.productImageUrl || undefined,
          productLink: request.questionFields.productLink || undefined,
          ...((request.selectedChannel.channelType as
            | PlatformType
            | undefined) &&
          HEPSIBURADA_SKU_CHANNEL_TYPES.includes(
            request.selectedChannel.channelType as PlatformType
          )
            ? { sku: request.questionFields.productCode || undefined }
            : {
                productMainId: request.questionFields.productCode || undefined
              }),
          ...(!request.selectedChannel.channelType && {
            sku: request.questionFields.productCode || undefined
          })
        }
      }),
      ...(request.simulationType === "chat" &&
        request.pageUrl.trim() && { pageUrl: request.pageUrl.trim() }),
      ...(request.selectedChannel.channelType && {
        platformType: request.selectedChannel.channelType as PlatformType,
        dbDriverId: request.selectedChannel._id
      })
    })

    setIsSending(false)

    if (!response.isSuccess || !response.data) {
      return
    }

    setMeta({
      conversationId:
        response.data.conversationId ?? options?.metaOverride?.conversationId,
      personId: response.data.personId ?? options?.metaOverride?.personId,
      aiPersonMemoryId:
        response.data.aiPersonMemoryId ??
        options?.metaOverride?.aiPersonMemoryId
    })

    const conversationIdForSteps = response.data.conversationId
    let aiMessageId: string | undefined
    if (conversationIdForSteps) {
      const aiMessagesResponse = await listAiMessagesQuery({
        conversationId: conversationIdForSteps,
        customerId,
        limit: 1,
        offset: 0
      })
      aiMessageId = aiMessagesResponse.isSuccess
        ? aiMessagesResponse.data?.docs?.[0]?._id || undefined
        : undefined
    }

    const assistantMessage: ChatMessage = {
      id: nextMessageId(),
      role: PreviousMessagesRole.Assistant,
      content: response.data?.content || "",
      aiMessageId
    }

    setMessages((prev) => [...prev, assistantMessage])
  }

  async function handleSend() {
    const queryText = inputValue.trim()
    if (
      !queryText ||
      !customerId ||
      !aiProfile?._id ||
      !selectedDbDriverId ||
      isSending
    ) {
      return
    }

    const userMessage: ChatMessage = {
      id: nextMessageId(),
      role: PreviousMessagesRole.User,
      content: queryText
    }
    const history = [...messages, userMessage].slice(-MAX_HISTORY)
    const request: LastSimulationRequest = {
      queryText,
      history: history.map(({ id, role, content }) => ({ id, role, content })),
      simulationType,
      commentFields,
      questionFields,
      pageUrl,
      selectedChannel
    }
    userMessage.request = request

    setMessages((prev) => [...prev, userMessage])
    setInputValue("")

    await runSimulation(request, { metaOverride: meta })
  }

  async function handleAskAgain(messageId: string) {
    if (isSending) {
      return
    }

    const targetIndex = messages.findIndex(
      (message) =>
        message.id === messageId && message.role === PreviousMessagesRole.User
    )
    if (targetIndex === -1) {
      return
    }

    const targetMessage = messages[targetIndex]
    if (!targetMessage.request) {
      return
    }

    if (meta.conversationId || meta.aiPersonMemoryId) {
      await deleteTestConversationMutation({
        conversationId: meta.conversationId,
        aiPersonMemoryId: meta.aiPersonMemoryId
      })
    }

    setMeta({})
    setMessages((prev) => prev.slice(0, targetIndex + 1))
    await runSimulation(targetMessage.request)
  }

  if (customerPicker && !customerId) {
    return (
      <Box sx={styles.containerWidth} p={3}>
        <Stack spacing={3}>
          {customerPicker}
          <Paper variant="outlined" sx={styles.noAccessCard}>
            <PersonOutlineIcon sx={{ fontSize: 36, color: "text.disabled" }} />
            <Typography variant="subtitle1" fontWeight={700}>
              {translate("ai_simulation")}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
              maxWidth={360}
            >
              {translate("ai_simulation_select_customer_prompt")}
            </Typography>
          </Paper>
        </Stack>
      </Box>
    )
  }

  if (!hasAiFeature) {
    return (
      <Box sx={styles.containerWidth} p={3}>
        <Stack spacing={3}>
          {customerPicker}
          <Paper variant="outlined" sx={styles.noAccessCard}>
            <LockOutlinedIcon sx={{ fontSize: 36, color: "text.disabled" }} />
            <Typography variant="subtitle1" fontWeight={700}>
              {translate("ai_simulation")}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
              maxWidth={360}
            >
              {translate("ai_simulation_no_access")}
            </Typography>
          </Paper>
        </Stack>
      </Box>
    )
  }

  const isInputDisabled = !aiProfile || isSending || !selectedDbDriverId
  const isSendDisabled = isInputDisabled || !inputValue.trim()

  return (
    <Box sx={styles.containerWidth} p={3}>
      <Stack spacing={3}>
        {customerPicker}
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Avatar sx={styles.headerAvatar}>
            <AutoAwesomeIcon fontSize="small" />
          </Avatar>
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>
              {translate("ai_simulation")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {translate("ai_simulation_subtitle")}
            </Typography>
          </Box>
        </Stack>

        <Paper variant="outlined" sx={styles.contextCard}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={1.5}
          >
            <Typography variant="subtitle2" fontWeight={600}>
              {translate("ai_simulation_templates")}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setShowSaveTemplateDialog(true)}
            >
              {translate("ai_simulation_save_as_template")}
            </Button>
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {allTemplates.map((template) => (
              <Tooltip
                key={template.id}
                title={translate("ai_simulation_apply_template")}
              >
                <Chip
                  icon={<span>{template.icon}</span>}
                  label={template.name}
                  onClick={() => applyTemplate(template)}
                  onDelete={
                    template.isCustom
                      ? () => deleteTemplate(template.id)
                      : undefined
                  }
                  color={template.isCustom ? "secondary" : "primary"}
                  variant={template.isCustom ? "outlined" : "filled"}
                  sx={{ cursor: "pointer" }}
                />
              </Tooltip>
            ))}
          </Stack>
        </Paper>

        <Dialog
          open={showSaveTemplateDialog}
          onClose={() => setShowSaveTemplateDialog(false)}
        >
          <DialogTitle>
            {translate("ai_simulation_save_as_template")}
          </DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              fullWidth
              label={translate("ai_simulation_template_name")}
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  saveAsTemplate()
                }
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowSaveTemplateDialog(false)}>
              {translate("cancel")}
            </Button>
            <Button
              variant="contained"
              disabled={!templateName.trim()}
              onClick={saveAsTemplate}
            >
              {translate("save")}
            </Button>
          </DialogActions>
        </Dialog>

        <Paper variant="outlined" sx={styles.contextCard}>
          <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
            <SearchIcon fontSize="small" color="action" />
            <Typography variant="subtitle2" fontWeight={600}>
              {translate("ai_simulation_by_message_id")}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
            <TextField
              size="small"
              placeholder={translate("ai_simulation_message_id_placeholder")}
              value={messageIdInput}
              sx={styles.fieldWidth}
              onChange={(e) => setMessageIdInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault()
                  handleFetchMessage()
                }
              }}
            />
            <Button
              variant="outlined"
              disabled={!messageIdInput.trim() || isFetchingMessage}
              onClick={() => handleFetchMessage()}
              startIcon={
                isFetchingMessage ? (
                  <CircularProgress size={14} />
                ) : (
                  <SearchIcon fontSize="small" />
                )
              }
            >
              {translate("ai_simulation_fetch")}
            </Button>
          </Stack>
          {fetchMessageError && (
            <Alert severity="error" sx={{ mt: 1.5 }}>
              {fetchMessageError}
            </Alert>
          )}
        </Paper>

        <ToggleButtonGroup
          exclusive
          color="primary"
          size="small"
          value={simulationType}
          sx={styles.typeSelector}
          onChange={(_, value: SimulationType | null) =>
            handleSimulationTypeChange(value)
          }
        >
          <ToggleButton value="chat">
            <ChatBubbleOutlineIcon fontSize="small" sx={{ mr: 1 }} />
            {translate("ai_simulation_type_chat")}
          </ToggleButton>
          <ToggleButton value="comment">
            <ModeCommentOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
            {translate("ai_simulation_type_comment")}
          </ToggleButton>
          <ToggleButton value="question">
            <HelpOutlineIcon fontSize="small" sx={{ mr: 1 }} />
            {translate("ai_simulation_type_question")}
          </ToggleButton>
        </ToggleButtonGroup>

        <Paper variant="outlined" sx={styles.contextCard}>
          <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
            <Typography variant="subtitle2" fontWeight={600}>
              {translate("ai_simulation_channel")} *
            </Typography>
          </Stack>
          <FormControl
            size="small"
            required
            error={!selectedDbDriverId}
            sx={styles.fieldWidth}
          >
            <InputLabel>{translate("ai_simulation_channel")}</InputLabel>
            <Select
              label={translate("ai_simulation_channel")}
              value={selectedDbDriverId}
              onChange={(e) => {
                setChannelWarning(undefined)
                setSelectedDbDriverId(e.target.value)
              }}
            >
              <MenuItem value="" disabled>
                <em>{translate("ai_simulation_select_channel_placeholder")}</em>
              </MenuItem>
              {groupedChannels.flatMap(([channelType, channelsOfType]) => [
                <ListSubheader key={`header-${channelType}`}>
                  {PLATFORM_ICONS[channelType as PlatformType] || ""}{" "}
                  {channelType}
                </ListSubheader>,
                ...channelsOfType.map((channel) => (
                  <MenuItem key={channel._id} value={channel._id ?? ""}>
                    {channel.name}
                  </MenuItem>
                ))
              ])}
            </Select>
            {!selectedDbDriverId && !channelWarning && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                {translate("ai_simulation_channel_required")}
              </Typography>
            )}
            {channelWarning && (
              <Typography
                variant="caption"
                color="warning.main"
                sx={{ mt: 0.5 }}
              >
                {channelWarning}
              </Typography>
            )}
          </FormControl>
          {selectedChannel?.channelType &&
            simulationType === "question" &&
            MARKETPLACE_LOOKUP_CHANNEL_TYPES.includes(
              selectedChannel.channelType as PlatformType
            ) && (
              <Alert severity="info" sx={{ mt: 2 }}>
                {translate("ai_simulation_live_lookup_hint")}
              </Alert>
            )}
        </Paper>

        {simulationType === "chat" && (
          <Paper variant="outlined" sx={styles.contextCard}>
            <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
              <ChatBubbleOutlineIcon fontSize="small" color="action" />
              <Typography variant="subtitle2" fontWeight={600}>
                {translate("ai_simulation_type_chat")}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
              <TextField
                label={translate("page_url")}
                value={pageUrl}
                sx={styles.fieldWidth}
                helperText={translate("page_url_helper")}
                onChange={(e) => setPageUrl(e.target.value)}
              />
            </Stack>
          </Paper>
        )}

        {simulationType === "comment" && (
          <Paper variant="outlined" sx={styles.contextCard}>
            <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
              <ModeCommentOutlinedIcon fontSize="small" color="action" />
              <Typography variant="subtitle2" fontWeight={600}>
                {translate("ai_simulation_type_comment")}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
              <TextField
                label={translate("post_description")}
                value={commentFields.postDescription}
                multiline
                minRows={2}
                sx={styles.fieldWidth}
                onChange={(e) =>
                  setCommentFields((prev) => ({
                    ...prev,
                    postDescription: e.target.value
                  }))
                }
              />
              <TextField
                label={translate("post_image_url")}
                value={commentFields.postImageUrl}
                sx={styles.fieldWidth}
                onChange={(e) =>
                  setCommentFields((prev) => ({
                    ...prev,
                    postImageUrl: e.target.value
                  }))
                }
              />
              <TextField
                label={translate("post_url")}
                value={commentFields.postUrl}
                sx={styles.fieldWidth}
                onChange={(e) =>
                  setCommentFields((prev) => ({
                    ...prev,
                    postUrl: e.target.value
                  }))
                }
              />
            </Stack>
          </Paper>
        )}

        {simulationType === "question" && (
          <Paper variant="outlined" sx={styles.contextCard}>
            <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
              <HelpOutlineIcon fontSize="small" color="action" />
              <Typography variant="subtitle2" fontWeight={600}>
                {translate("ai_simulation_type_question")}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
              <TextField
                label={translate("product_name")}
                value={questionFields.productName}
                sx={styles.fieldWidth}
                onChange={(e) =>
                  setQuestionFields((prev) => ({
                    ...prev,
                    productName: e.target.value
                  }))
                }
              />
              <TextField
                label={translate("product_image_url")}
                value={questionFields.productImageUrl}
                sx={styles.fieldWidth}
                onChange={(e) =>
                  setQuestionFields((prev) => ({
                    ...prev,
                    productImageUrl: e.target.value
                  }))
                }
              />
              <TextField
                label={translate("product_link")}
                value={questionFields.productLink}
                sx={styles.fieldWidth}
                onChange={(e) =>
                  setQuestionFields((prev) => ({
                    ...prev,
                    productLink: e.target.value
                  }))
                }
              />
              <TextField
                label={translate("product_code")}
                value={questionFields.productCode}
                sx={styles.fieldWidth}
                helperText={translate("product_code_helper")}
                onChange={(e) =>
                  setQuestionFields((prev) => ({
                    ...prev,
                    productCode: e.target.value
                  }))
                }
              />
            </Stack>
          </Paper>
        )}

        <Paper variant="outlined" sx={styles.chatContainer}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={styles.chatHeader}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar src={aiProfile?.avatar || undefined} sx={styles.aiAvatar}>
                <SmartToyOutlinedIcon fontSize="small" />
              </Avatar>
              <Box>
                <Typography variant="subtitle2" fontWeight={600}>
                  {aiProfile?.name || translate("ai_simulation")}
                </Typography>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Box sx={styles.statusDot} />
                  <Typography variant="caption" color="text.secondary">
                    {translate("ai_simulation_test_mode")}
                  </Typography>
                </Stack>
              </Box>
            </Stack>
            <Tooltip title={translate("reset")}>
              <span>
                <IconButton
                  size="small"
                  disabled={isSending}
                  onClick={handleReset}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
          <Divider />

          <Box sx={styles.messagesArea}>
            {!isAiProfileLoading && !aiProfile && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {translate("ai_simulation_no_ai_profile")}
              </Alert>
            )}
            {messages.length === 0 && (
              <Box sx={styles.emptyState}>
                <ForumOutlinedIcon sx={{ fontSize: 40 }} />
                <Typography variant="body2">
                  {translate("ai_simulation_empty_state")}
                </Typography>
              </Box>
            )}
            {messages.map((message) => {
              const isUser = message.role === PreviousMessagesRole.User
              return (
                <Box
                  key={message.id}
                  sx={
                    isUser ? styles.userMessageRow : styles.assistantMessageRow
                  }
                >
                  <Avatar
                    sx={
                      isUser ? styles.bubbleAvatarUser : styles.bubbleAvatarAi
                    }
                  >
                    {isUser ? (
                      <PersonOutlineIcon sx={{ fontSize: 16 }} />
                    ) : (
                      <SmartToyOutlinedIcon sx={{ fontSize: 16 }} />
                    )}
                  </Avatar>
                  <Box sx={styles.messageContentColumn}>
                    <Box
                      sx={isUser ? styles.userBubble : styles.assistantBubble}
                    >
                      <Typography variant="body2">{message.content}</Typography>
                    </Box>
                    <Box
                      sx={
                        isUser
                          ? styles.userMessageActions
                          : styles.assistantMessageActions
                      }
                    >
                      {isUser && message.request && (
                        <Tooltip title={translate("ai_simulation_ask_again")}>
                          <span>
                            <IconButton
                              size="small"
                              disabled={isSending}
                              onClick={() => handleAskAgain(message.id)}
                              sx={styles.messageActionButton}
                            >
                              <RefreshIcon fontSize="inherit" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      {!isUser && (message.aiMessageId || message.answerId) && (
                        <Tooltip title={translate("ai_simulation_view_steps")}>
                          <IconButton
                            size="small"
                            sx={styles.messageActionButton}
                            onClick={() => {
                              if (message.aiMessageId) {
                                setStepsAnswerId(undefined)
                                setStepsAiMessageId(message.aiMessageId)
                              } else if (message.answerId) {
                                // No known run id for a historical message — the
                                // resolver ignores aiMessageId once answerId
                                // resolves, so any valid ObjectId placeholder works.
                                setStepsAnswerId(message.answerId)
                                setStepsAiMessageId(message.answerId)
                              }
                            }}
                          >
                            <TimelineIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                </Box>
              )
            })}
            {isSending && (
              <Box sx={styles.assistantMessageRow}>
                <Avatar sx={styles.bubbleAvatarAi}>
                  <SmartToyOutlinedIcon sx={{ fontSize: 16 }} />
                </Avatar>
                <Box sx={styles.assistantBubble}>
                  <CircularProgress size={14} />
                </Box>
              </Box>
            )}
            <div ref={messagesEndRef} />
          </Box>
          <Divider />

          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={styles.inputRow}
          >
            <TextField
              fullWidth
              size="small"
              sx={styles.pillInput}
              placeholder={translate("ai_simulation_input_placeholder")}
              value={inputValue}
              disabled={isInputDisabled}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault()
                  handleSend()
                }
              }}
            />
            <IconButton
              disabled={isSendDisabled}
              onClick={handleSend}
              sx={
                isSendDisabled
                  ? styles.sendButtonDisabled
                  : styles.sendButtonEnabled
              }
            >
              <SendIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={styles.contextCard}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={1.5}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <BoltIcon fontSize="small" color="action" />
              <Typography variant="subtitle2" fontWeight={600}>
                {translate("ai_simulation_actions")}
              </Typography>
            </Stack>
            <Button
              size="small"
              variant="outlined"
              onClick={() => refetchActions()}
              startIcon={
                isActionsLoading ? <CircularProgress size={14} /> : undefined
              }
            >
              {isActionsFetched
                ? translate("ai_simulation_refresh")
                : translate("ai_simulation_fetch")}
            </Button>
          </Stack>
          {isActionsFetched && (
            <TextField
              size="small"
              fullWidth
              placeholder={translate("ai_simulation_search_placeholder")}
              value={actionsSearchInput}
              onChange={(e) => setActionsSearchInput(e.target.value)}
              sx={{ mb: 1.5 }}
              InputProps={{
                startAdornment: (
                  <SearchIcon
                    fontSize="small"
                    sx={{ mr: 1, color: "action.active" }}
                  />
                )
              }}
            />
          )}
          {isActionsFetched && filteredActions.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              {translate("ai_simulation_no_actions")}
            </Typography>
          )}
          {filteredActions.length > 0 && (
            <Stack spacing={1} sx={{ maxHeight: 220, overflowY: "auto" }}>
              {filteredActions.map((action) => {
                const eligible = isActionEligible(action, selectedDbDriverId)
                return (
                  <Stack
                    key={action._id}
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    onClick={() => setSelectedAction(action)}
                    sx={{
                      py: 0.75,
                      px: 1.25,
                      borderRadius: 1.5,
                      backgroundColor: "grey.50",
                      cursor: "pointer",
                      "&:hover": { backgroundColor: "grey.100" }
                    }}
                  >
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {action.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {action.type}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={
                        eligible
                          ? translate("ai_simulation_eligible")
                          : translate("ai_simulation_not_eligible")
                      }
                      color={eligible ? "success" : "default"}
                      variant={eligible ? "filled" : "outlined"}
                    />
                  </Stack>
                )
              })}
            </Stack>
          )}
          {isActionsFetched && (
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              mt={1.5}
            >
              <Typography variant="caption" color="text.secondary">
                {translate("ai_simulation_page")} {actionsPage} ·{" "}
                {actionsData?.totalRecords ?? 0}{" "}
                {translate("ai_simulation_total")}
              </Typography>
              <Stack direction="row" spacing={0.5}>
                <IconButton
                  size="small"
                  disabled={actionsPage <= 1 || isActionsLoading}
                  onClick={() => setActionsPage((p) => Math.max(1, p - 1))}
                >
                  <NavigateBeforeIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  disabled={!actionsData?.hasNextPage || isActionsLoading}
                  onClick={() => setActionsPage((p) => p + 1)}
                >
                  <NavigateNextIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Stack>
          )}
        </Paper>

        <Paper variant="outlined" sx={styles.contextCard}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={1.5}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <RuleIcon fontSize="small" color="action" />
              <Typography variant="subtitle2" fontWeight={600}>
                {translate("ai_simulation_guidance")}
              </Typography>
            </Stack>
            <Button
              size="small"
              variant="outlined"
              onClick={() => refetchGuidance()}
              startIcon={
                isGuidanceLoading ? <CircularProgress size={14} /> : undefined
              }
            >
              {isGuidanceFetched
                ? translate("ai_simulation_refresh")
                : translate("ai_simulation_fetch")}
            </Button>
          </Stack>
          {isGuidanceFetched && (
            <TextField
              size="small"
              fullWidth
              placeholder={translate("ai_simulation_search_placeholder")}
              value={guidanceSearchInput}
              onChange={(e) => setGuidanceSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setGuidanceAppliedSearch(guidanceSearchInput.trim())
                }
              }}
              sx={{ mb: 1.5 }}
              InputProps={{
                startAdornment: (
                  <SearchIcon
                    fontSize="small"
                    sx={{ mr: 1, color: "action.active" }}
                  />
                ),
                endAdornment: (
                  <IconButton
                    size="small"
                    onClick={() =>
                      setGuidanceAppliedSearch(guidanceSearchInput.trim())
                    }
                  >
                    <SearchIcon fontSize="small" />
                  </IconButton>
                )
              }}
            />
          )}
          {isGuidanceFetched && (guidanceData?.length ?? 0) === 0 && (
            <Typography variant="body2" color="text.secondary">
              {translate("ai_simulation_no_guidance")}
            </Typography>
          )}
          {(guidanceData?.length ?? 0) > 0 && (
            <Stack spacing={1} sx={{ maxHeight: 220, overflowY: "auto" }}>
              {guidanceData!.map((guidance) => {
                const eligible = isGuidanceEligible(
                  guidance,
                  selectedDbDriverId,
                  stepTestMsgType
                )
                return (
                  <Stack
                    key={guidance._id}
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    onClick={() => setSelectedGuidance(guidance)}
                    sx={{
                      py: 0.75,
                      px: 1.25,
                      borderRadius: 1.5,
                      backgroundColor: "grey.50",
                      cursor: "pointer",
                      "&:hover": { backgroundColor: "grey.100" }
                    }}
                  >
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {guidance.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {guidance.category}
                        {guidance.status !== AiGuidanceStatusEnum.Active &&
                          ` · ${guidance.status}`}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={
                        eligible
                          ? translate("ai_simulation_eligible")
                          : translate("ai_simulation_not_eligible")
                      }
                      color={eligible ? "success" : "default"}
                      variant={eligible ? "filled" : "outlined"}
                    />
                  </Stack>
                )
              })}
            </Stack>
          )}
        </Paper>

        <Paper variant="outlined" sx={styles.contextCard}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={1.5}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <MenuBookIcon fontSize="small" color="action" />
              <Typography variant="subtitle2" fontWeight={600}>
                {translate("ai_simulation_knowledge_sources")}
              </Typography>
            </Stack>
            <Button
              size="small"
              variant="outlined"
              onClick={() => refetchKnowledge()}
              startIcon={
                isKnowledgeLoading ? <CircularProgress size={14} /> : undefined
              }
            >
              {isKnowledgeFetched
                ? translate("ai_simulation_refresh")
                : translate("ai_simulation_fetch")}
            </Button>
          </Stack>
          {isKnowledgeFetched && (
            <Stack spacing={1.5} sx={{ mb: 1.5 }}>
              <TextField
                size="small"
                fullWidth
                placeholder={translate("ai_simulation_search_placeholder")}
                value={knowledgeSearchInput}
                onChange={(e) => setKnowledgeSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    setKnowledgePage(1)
                    setKnowledgeAppliedSearch(knowledgeSearchInput.trim())
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <SearchIcon
                      fontSize="small"
                      sx={{ mr: 1, color: "action.active" }}
                    />
                  ),
                  endAdornment: (
                    <IconButton
                      size="small"
                      onClick={() => {
                        setKnowledgePage(1)
                        setKnowledgeAppliedSearch(knowledgeSearchInput.trim())
                      }}
                    >
                      <SearchIcon fontSize="small" />
                    </IconButton>
                  )
                }}
              />
              <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel>{translate("ai_simulation_status")}</InputLabel>
                  <Select
                    label={translate("ai_simulation_status")}
                    value={knowledgeStatusFilter}
                    onChange={(e) => {
                      setKnowledgePage(1)
                      setKnowledgeStatusFilter(
                        e.target.value as AiKnowledgeSourcesStatus | ""
                      )
                    }}
                  >
                    <MenuItem value="">
                      <em>{translate("ai_simulation_all")}</em>
                    </MenuItem>
                    {Object.values(AiKnowledgeSourcesStatus).map((status) => (
                      <MenuItem key={status} value={status}>
                        {status}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel>
                    {translate("ai_simulation_source_type")}
                  </InputLabel>
                  <Select
                    label={translate("ai_simulation_source_type")}
                    value={knowledgeSourceTypeFilter}
                    onChange={(e) => {
                      setKnowledgePage(1)
                      setKnowledgeSourceTypeFilter(
                        e.target.value as AiKnowledgeSourcesSourceTypes | ""
                      )
                    }}
                  >
                    <MenuItem value="">
                      <em>{translate("ai_simulation_all")}</em>
                    </MenuItem>
                    {Object.values(AiKnowledgeSourcesSourceTypes).map(
                      (sourceType) => (
                        <MenuItem key={sourceType} value={sourceType}>
                          {sourceType}
                        </MenuItem>
                      )
                    )}
                  </Select>
                </FormControl>
              </Stack>
            </Stack>
          )}
          {isKnowledgeFetched && (knowledgeData?.docs.length ?? 0) === 0 && (
            <Typography variant="body2" color="text.secondary">
              {translate("ai_simulation_no_knowledge_sources")}
            </Typography>
          )}
          {(knowledgeData?.docs.length ?? 0) > 0 && (
            <Stack spacing={1} sx={{ maxHeight: 220, overflowY: "auto" }}>
              {knowledgeData!.docs.map((source) => (
                <Stack
                  key={source._id}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  onClick={() => setSelectedKnowledge(source)}
                  sx={{
                    py: 0.75,
                    px: 1.25,
                    borderRadius: 1.5,
                    backgroundColor: "grey.50",
                    cursor: "pointer",
                    "&:hover": { backgroundColor: "grey.100" }
                  }}
                >
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      {source.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {source.sourceType}
                    </Typography>
                  </Box>
                  {source.status && (
                    <Chip
                      size="small"
                      label={source.status}
                      color={KNOWLEDGE_STATUS_COLOR[source.status] || "default"}
                    />
                  )}
                </Stack>
              ))}
            </Stack>
          )}
          {isKnowledgeFetched && (
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              mt={1.5}
            >
              <Typography variant="caption" color="text.secondary">
                {translate("ai_simulation_page")} {knowledgePage} ·{" "}
                {knowledgeData?.totalDocs ?? 0}{" "}
                {translate("ai_simulation_total")}
              </Typography>
              <Stack direction="row" spacing={0.5}>
                <IconButton
                  size="small"
                  disabled={!knowledgeData?.hasPrevPage || isKnowledgeLoading}
                  onClick={() => setKnowledgePage((p) => Math.max(1, p - 1))}
                >
                  <NavigateBeforeIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  disabled={!knowledgeData?.hasNextPage || isKnowledgeLoading}
                  onClick={() => setKnowledgePage((p) => p + 1)}
                >
                  <NavigateNextIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Stack>
          )}
        </Paper>
      </Stack>

      <Dialog
        open={!!selectedAction}
        onClose={() => setSelectedAction(null)}
        maxWidth="sm"
        fullWidth
      >
        {selectedAction && (
          <>
            <DialogTitle>{selectedAction.name}</DialogTitle>
            <DialogContent>
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1}>
                  <Chip size="small" label={selectedAction.type} />
                  {selectedAction.method && (
                    <Chip size="small" label={selectedAction.method} />
                  )}
                </Stack>
                {selectedAction.description && (
                  <Typography variant="body2">
                    {selectedAction.description}
                  </Typography>
                )}
                {selectedAction.instructions && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {translate("ai_simulation_action_instructions")}
                    </Typography>
                    <Typography variant="body2">
                      {selectedAction.instructions}
                    </Typography>
                  </Box>
                )}
                {selectedAction.url && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      URL
                    </Typography>
                    <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                      {selectedAction.url}
                    </Typography>
                  </Box>
                )}
                {!!selectedAction.headers?.length && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Headers
                    </Typography>
                    {selectedAction.headers.map((header, index) => (
                      <Typography variant="body2" key={index}>
                        {header?.key}: {header?.value}
                      </Typography>
                    ))}
                  </Box>
                )}
                {selectedAction.body && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Body
                    </Typography>
                    <Paper
                      variant="outlined"
                      sx={{ p: 1.5, backgroundColor: "grey.50" }}
                    >
                      <Typography
                        variant="body2"
                        component="pre"
                        sx={{
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-all",
                          fontFamily: "monospace"
                        }}
                      >
                        {selectedAction.body}
                      </Typography>
                    </Paper>
                  </Box>
                )}
                {!!selectedAction.collectInfo?.length && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {translate("ai_simulation_action_collect_info")}
                    </Typography>
                    {selectedAction.collectInfo.map((field) => (
                      <Typography variant="body2" key={field?._id}>
                        {field?.name}
                        {field?.description ? ` — ${field.description}` : ""}
                      </Typography>
                    ))}
                  </Box>
                )}
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {translate("ai_simulation_channel")}
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={0.5}
                    flexWrap="wrap"
                    useFlexGap
                  >
                    {selectedAction.channelIds?.length ? (
                      selectedAction.channelIds.map((id) => (
                        <Chip
                          key={id}
                          size="small"
                          label={resolveChannelName(id || "")}
                          color={
                            id === selectedDbDriverId ? "success" : "default"
                          }
                        />
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </Stack>
                </Box>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedAction(null)}>
                {translate("cancel")}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Dialog
        open={!!selectedGuidance}
        onClose={() => setSelectedGuidance(null)}
        maxWidth="sm"
        fullWidth
      >
        {selectedGuidance && (
          <>
            <DialogTitle>{selectedGuidance.title}</DialogTitle>
            <DialogContent>
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1}>
                  <Chip size="small" label={selectedGuidance.category} />
                  <Chip
                    size="small"
                    label={selectedGuidance.status}
                    color={
                      selectedGuidance.status === AiGuidanceStatusEnum.Active
                        ? "success"
                        : "default"
                    }
                  />
                </Stack>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  {selectedGuidance.content}
                </Typography>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {translate("ai_simulation_channel")}
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={0.5}
                    flexWrap="wrap"
                    useFlexGap
                  >
                    {selectedGuidance.channel?.length ? (
                      selectedGuidance.channel.map((id) => (
                        <Chip
                          key={id}
                          size="small"
                          label={resolveChannelName(id || "")}
                          color={
                            id === selectedDbDriverId ? "success" : "default"
                          }
                        />
                      ))
                    ) : (
                      <Chip
                        size="small"
                        label={translate("ai_simulation_all_channels")}
                      />
                    )}
                  </Stack>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {translate("ai_simulation_message_types")}
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={0.5}
                    flexWrap="wrap"
                    useFlexGap
                  >
                    {selectedGuidance.messageTypes?.length ? (
                      selectedGuidance.messageTypes.map((mt) => (
                        <Chip key={mt} size="small" label={mt} />
                      ))
                    ) : (
                      <Chip
                        size="small"
                        label={translate("ai_simulation_all_message_types")}
                      />
                    )}
                  </Stack>
                </Box>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedGuidance(null)}>
                {translate("cancel")}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Dialog
        open={!!selectedKnowledge}
        onClose={() => setSelectedKnowledge(null)}
        maxWidth="sm"
        fullWidth
      >
        {selectedKnowledge && (
          <>
            <DialogTitle>{selectedKnowledge.title}</DialogTitle>
            <DialogContent>
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1}>
                  {selectedKnowledge.sourceType && (
                    <Chip size="small" label={selectedKnowledge.sourceType} />
                  )}
                  {selectedKnowledge.status && (
                    <Chip
                      size="small"
                      label={selectedKnowledge.status}
                      color={
                        KNOWLEDGE_STATUS_COLOR[selectedKnowledge.status] ||
                        "default"
                      }
                    />
                  )}
                </Stack>
                {selectedKnowledge.question && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {translate("ai_simulation_question")}
                    </Typography>
                    <Typography variant="body2">
                      {selectedKnowledge.question}
                    </Typography>
                  </Box>
                )}
                {selectedKnowledge.answer && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {translate("ai_simulation_answer")}
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                      {selectedKnowledge.answer}
                    </Typography>
                  </Box>
                )}
                {selectedKnowledge.source && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {translate("ai_simulation_source")}
                    </Typography>
                    <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                      {selectedKnowledge.source}
                    </Typography>
                  </Box>
                )}
                {selectedKnowledge.failedReason && (
                  <Alert severity="error">
                    {selectedKnowledge.failedReason}
                  </Alert>
                )}
                <Box>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={1}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {translate("ai_simulation_content")}
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => refetchKnowledgeContent()}
                      startIcon={
                        isKnowledgeContentLoading ? (
                          <CircularProgress size={14} />
                        ) : undefined
                      }
                    >
                      {isKnowledgeContentFetched
                        ? translate("ai_simulation_refresh")
                        : translate("ai_simulation_fetch")}
                    </Button>
                  </Stack>
                  {isKnowledgeContentFetched &&
                    (knowledgeContentData?.length ?? 0) === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        {translate("ai_simulation_no_content")}
                      </Typography>
                    )}
                  {!!knowledgeContentData?.length && (
                    <Stack
                      spacing={1.5}
                      sx={{ maxHeight: 300, overflowY: "auto" }}
                    >
                      {knowledgeContentData.map((chunk) => (
                        <Paper
                          key={chunk._id}
                          variant="outlined"
                          sx={{ p: 1.5, backgroundColor: "grey.50" }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ whiteSpace: "pre-wrap" }}
                          >
                            {chunk.content}
                          </Typography>
                        </Paper>
                      ))}
                    </Stack>
                  )}
                </Box>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedKnowledge(null)}>
                {translate("cancel")}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Drawer
        anchor="right"
        open={!!stepsAiMessageId}
        onClose={() => {
          setStepsAiMessageId(null)
          setStepsAnswerId(undefined)
        }}
      >
        <AiStepsDrawer
          open={!!stepsAiMessageId}
          onClose={() => {
            setStepsAiMessageId(null)
            setStepsAnswerId(undefined)
          }}
          steps={stepsData}
          isLoading={isStepsLoading}
          runId={stepsAiMessageId}
        />
      </Drawer>
    </Box>
  )
}
